import { useEffect } from "react";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { isInvalidCredential, useSignOut } from "@newHooks/auth/useSignOut";
import { useSessionContext } from "@newHooks/context/session/session.context";
import { RestUrl } from "@maven-rest/common/cyan.types";
import { HttpRequest, RestError } from "@maven-rest/common/HttpRequest";

type QueryOptions<TQueryData, TData> = Omit<UseQueryOptions<TQueryData, RestError, TData>, "initialData"> & {
  initialData?: () => undefined;
};

type BaseQueryOptions<TQueryData, TData, TVariables> = {
  fnOption?: {
    host?: string;
    url: RestUrl;
    request?: TVariables;
    query?: Record<string, string>;
  };
  allowWithoutSession?: boolean;
  onSuccess?: (data: TQueryData) => void;
  onError?: (error: RestError) => void;
} & QueryOptions<TQueryData, TData>;

export type SimpleQueryOptions<TData, TRequest = undefined> = BaseQueryOptions<TData, TData, TRequest>;

export const useBaseQuery = <TQueryData, TData = TQueryData, TVariables = undefined>({
  fnOption,
  onError,
  onSuccess,
  ...queryOptions
}: BaseQueryOptions<TQueryData, TData, TVariables>) => {
  const { session } = useSessionContext();
  const signOut = useSignOut();

  const query = useQuery<TQueryData, RestError, TData>({
    queryFn: fnOption?.url
      ? async () => {
          const resp = await HttpRequest.request<TQueryData>(
            fnOption.host || process.env.REACT_APP_DOCS_BE_URL || "",
            fnOption.url?.[0] || "GET",
            `${fnOption.url[1]}${fnOption.query ? `?${new URLSearchParams(fnOption.query)}` : ""}`,
            fnOption.request,
            {}
          );

          onSuccess && onSuccess(resp.data);

          return resp.data;
        }
      : queryOptions.queryFn,
    enabled: (queryOptions.allowWithoutSession || !!session) && queryOptions.enabled,
    ...queryOptions,
  });

  useEffect(() => {
    if (!query.isError) return;
    if (isInvalidCredential(query.error)) {
      signOut();
      return;
    }
    if (onError) {
      onError(query.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isError]);

  return query;
};
